import { useMutation } from '@apollo/client';
import { useCurrentAccount } from '@application/api';
import { Analytics } from '@application/utils/analytics';
import { graphql } from '@gql/gql';
import type {
  MtLitterCreateLitterMutation,
  MtLitterCreateLitterMutationVariables,
} from '@gql/graphql';
import type { MtLitterPageFormValues } from '@presentation/pages';
import cleanDeep from 'clean-deep';
import { useCallback } from 'react';
import { MT_LITTERS_GET_LITTERS } from '../../MtLittersPageController/api/useGetLitters';
import { MT_BREEDER_ONBOARDING_GET_PROFILE } from '../../MtBreederOnboardingPageController/api/useGetProfile';
import { useLitterCreateBuilder } from './useLitterCreateBuilder';

export const MT_LITTER_CREATE_LITTER = graphql(`
  mutation MtLitterCreateLitter($data: CreateLitterInput!, $imageUpload: [Upload!]) {
    createLitter(data: $data, imagesUpload: $imageUpload) {
      id
    }
  }
`);

export const useCreateLitter = () => {
  const [createLitter, { error, loading }] = useMutation<
    MtLitterCreateLitterMutation,
    MtLitterCreateLitterMutationVariables
  >(MT_LITTER_CREATE_LITTER, {
    refetchQueries: [MT_LITTERS_GET_LITTERS, MT_BREEDER_ONBOARDING_GET_PROFILE],
  });

  const { currentAccountLoading, currentAccountError } = useCurrentAccount();
  const { litterCreateBuilder } = useLitterCreateBuilder();

  const createLitterFromForm = useCallback(
    (form: MtLitterPageFormValues) => {
      const { litterData } = litterCreateBuilder(form);

      const imageUpload: Scalar_Upload[] = cleanDeep(form.images ?? [])
        ?.map((file) => (typeof file === 'string' ? undefined : (file as Scalar_Upload)))
        .filter((file) => file != null) as Scalar_Upload[];

      Analytics.sendPuppyCreatedEvent();

      return createLitter({
        variables: {
          ...(imageUpload?.length ? { imageUpload } : {}),
          data: litterData,
        },
      });
    },
    [createLitter, litterCreateBuilder],
  );

  return {
    createLitterLoading: loading || currentAccountLoading,
    createLitterError: error || currentAccountError,
    createLitterFromForm,
    createLitter,
  };
};
